/** Window events for the shared Messenger surface (WC-V20-HOST). */

export const MESSENGER_OPEN_EVENT = "bb-messenger-open";
export const MESSENGER_CLOSE_EVENT = "bb-messenger-close";
export const MESSENGER_TOGGLE_EVENT = "bb-messenger-toggle";
export const MESSENGER_STATE_EVENT = "bb-messenger-state";

/** sessionStorage — survives mode remounts within the same tab session. */
export const MESSENGER_OPEN_SESSION_KEY = "bb.messenger.open.v1";

export type MessengerOpenDetail = {
  /** Bypass assist / other gates — always open when dispatched from UI chrome. */
  force?: boolean;
};

export type MessengerStateDetail = {
  open: boolean;
};

export function dispatchMessengerOpen(detail: MessengerOpenDetail = { force: true }): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<MessengerOpenDetail>(MESSENGER_OPEN_EVENT, { detail }));
}

export function dispatchMessengerClose(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(MESSENGER_CLOSE_EVENT));
}

export function dispatchMessengerToggle(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(MESSENGER_TOGGLE_EVENT));
}

export function readMessengerOpenSession(): boolean {
  if (typeof sessionStorage === "undefined") return false;
  try {
    return sessionStorage.getItem(MESSENGER_OPEN_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeMessengerOpenSession(open: boolean): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    if (open) sessionStorage.setItem(MESSENGER_OPEN_SESSION_KEY, "1");
    else sessionStorage.removeItem(MESSENGER_OPEN_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

export function publishMessengerState(open: boolean): void {
  writeMessengerOpenSession(open);
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<MessengerStateDetail>(MESSENGER_STATE_EVENT, { detail: { open } })
  );
}

export function subscribeMessengerState(onChange: (open: boolean) => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<MessengerStateDetail>).detail;
    onChange(Boolean(detail?.open));
  };
  window.addEventListener(MESSENGER_STATE_EVENT, handler);
  return () => window.removeEventListener(MESSENGER_STATE_EVENT, handler);
}
